import { Check } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate } from 'react-router-dom';

interface PricingCardProps {
  title: string;
  price: string;
  description: string;
  features: string[];
  buttonText: string;
  isOneTime: boolean;
  isPopular?: boolean;
  isPreference?: boolean;
}

function PricingCard({ title, price, description, features, buttonText, isOneTime, isPopular, isPreference }: PricingCardProps) {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const handleNavigation = (path: string) => (e: React.MouseEvent) => {
    e.preventDefault();
    navigate(path);
  };

  return (
    <div className={`relative p-8 md:p-10 rounded-2xl transition-all duration-300 ${
      isPopular 
        ? 'bg-gradient-to-b from-[#0D3C4C] to-[#165469] text-white shadow-xl border-2 border-teal-500' 
        : ' border-2 border-teal-500'
    }`}>
      {isPreference && (
        <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-teal-500 text-white px-4 py-1 rounded-full text-sm font-semibold">
          {t.pricing.mostPopular}
        </div>
      )}
      <div className="flex flex-col md:flex-row md:items-center md:gap-12">
        {/* Left: Price & description */}
        <div className="md:w-1/3 mb-8 md:mb-0">
          <h4 className={`text-2xl font-bold mb-2 ${isPopular ? 'text-white' : 'text-[#0D3C4C]'}`}>{title}</h4>
          <div className="flex items-baseline mb-4">
            <span className={`text-5xl font-bold ${isPopular ? 'text-white' : 'text-[#0D3C4C]'}`}>${price}</span>
            <span className={`ml-2 ${isPopular ? 'text-gray-300' : 'text-gray-600'}`}>
              /{t.pricing.plan?.month || (isOneTime ? t.pricing.app.oneTime : t.pricing.app.month)}
            </span>
          </div>
          <p className={`mb-6 ${isPopular ? 'text-gray-300' : 'text-gray-600'}`}>{description}</p>
          <button
            onClick={handleNavigation('/login')}
            className={`w-full py-4 rounded-xl font-semibold transition-colors ${
              isPopular 
                ? 'bg-white text-[#0D3C4C] hover:bg-gray-100' 
                : 'bg-[#0D3C4C] text-white hover:bg-[#165469]'
            }`}>
            {buttonText}
          </button>
        </div>
        {/* Right: Features */}
        <div className="md:w-2/3">
          <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {features.map((feature, index) => (
              <li key={index} className="flex items-center">
                <Check className={`mr-3 flex-shrink-0 ${isPopular ? 'text-teal-400' : 'text-teal-500'}`} size={20} />
                <span className={isPopular ? 'text-gray-300' : 'text-gray-600'}>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
}

export function Pricing() {
  const { t } = useLanguage();

  return (
    <section id="pricing" className="py-32">
      <div className="container mx-auto px-4">
        <div className="max-w-3xl mx-auto text-center mb-20">
          <h3 className="text-4xl font-bold text-[#0D3C4C] mb-6">
            {t.pricing.title}
          </h3>
          <p className="text-xl text-gray-600">
            {t.pricing.subtitle}
          </p>
        </div>
        <div className="max-w-5xl mx-auto">
          <PricingCard 
            title={t.pricing.plan.title}
            price={t.pricing.plan.price}
            description={t.pricing.plan.description}
            features={t.pricing.plan.features}
            buttonText={t.pricing.plan.buttonText}
            isOneTime={false}
            isPopular={true}
            isPreference={true}
          />
        </div>
      </div>
    </section>
  );
}